import { useMemo } from 'react';
import { formatControlStatus } from '../../utils/format-control-status/format-control-status.util';
import { useAnnotatedSchemaProps } from '../use-annotated-schema-props/use-annotated-schema-props';
import type {
  EntityFormControlProps,
  ControlProps,
} from '../../types/controls';
import { getComposedSchemaPropertyPath } from '../../utils/get-composed-schema-property-path/get-composed-schema-property-path';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

/**
 * This hook takes the default props passed to controls via @jsonforms/react
 * withJsonFormsControlProps and enhances them with props from our own custom
 * annotations implementation, which are present in `props.schema`
 *
 * @param props - ControlProps passed to all controls by JsonSchemaDispatch
 * @returns {@link EntityFormControlProps}
 */

export const useEntityFormControl = (
  props: ControlProps,
): EntityFormControlProps => {
  /**
   * transform custom annotations into props for our form controls and
   * get translated strings
   */
  const entityDetailsCtx = useEntityDetailsProps();
  const formPath = entityDetailsCtx?.formPath;

  const annotationProps = useAnnotatedSchemaProps(
    props.schema,
    props,
    getComposedSchemaPropertyPath(formPath, props.path),
  );

  const { statusMessage, inputStatus } = formatControlStatus({
    errors: props.errors,
    label: annotationProps.label,
  });

  return useMemo(
    () => ({
      ...props,
      ...annotationProps,
      statusMessage,
      inputStatus,
    }),
    [props, annotationProps, statusMessage, inputStatus],
  );
};
